import { EQ_BANDS, positionToDb, type EqBand, type Mixer } from "../audio/mixer.js";
import { downloadBlob, LONG_RECORDING_SECONDS, MixRecorder } from "../audio/recorder.js";
import { Fader, Knob } from "./controls.js";
import { duration as formatDuration, escapeHtml } from "./format.js";

const BAND_LABELS: Record<EqBand, string> = { low: "LOW", mid: "MID", high: "HIGH" };

const COLLAPSE_KEY = "wldj.mixerCollapsed";

function formatDb(position: number): string {
  const db = positionToDb(position);
  if (db <= -39.5) return "kill";
  if (Math.abs(db) < 0.05) return "0.0";
  return `${db > 0 ? "+" : ""}${db.toFixed(1)}`;
}

function formatLevel(position: number): string {
  if (position <= 0.001) return "−∞";
  // Squared before it reaches the gain node, so report the resulting level.
  return `${(20 * Math.log10(position * position)).toFixed(1)}`;
}

export class MixerPanel {
  private readonly recorder: MixRecorder;
  /** Notified when the operator touches the mixer, so auto-mix can yield (X2). */
  onManualControl: (() => void) | null = null;

  private readonly eqKnobs = new Map<string, Knob>();
  private readonly trimFaders = new Map<string, Fader>();
  private masterFader!: Fader;
  private peakHold = 0;
  private peakHoldUntil = 0;
  private clipUntil = 0;
  private collapsed = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly mixer: Mixer,
    private readonly channelIds: [string, string],
  ) {
    this.recorder = new MixRecorder(mixer.monitorPoint);
    this.root.classList.add("mixer");
    this.root.innerHTML = this.chrome();
    this.buildControls();
    this.bind();
    this.render();

    let remembered: string | null = null;
    try {
      remembered = localStorage.getItem(COLLAPSE_KEY);
    } catch {
      /* private browsing */
    }
    this.setCollapsed(remembered === "1");
  }

  private chrome(): string {
    const [a, b] = this.channelIds;
    const body = this.strips(a, b);
    return `
      <div class="panel-head">
        <span class="panel-title">Mixer</span>
        <button id="mx-collapse" type="button" aria-expanded="true">Hide</button>
      </div>
      <div class="panel-body">${body}</div>
    `;
  }

  private strips(a: string, b: string): string {
    const strip = (id: string) => `
      <div class="strip" data-strip="${id}">
        <div class="strip-head">
          <span class="strip-id">${escapeHtml(id)}</span>
          <button data-act="eq-reset" data-ch="${id}" type="button" title="Flatten EQ">flat</button>
        </div>
        <div class="eq-row" data-eq-row="${id}"></div>
        <div class="trim-row" data-trim-row="${id}"></div>
      </div>`;

    return `
      <div class="mixer-strips">
        ${strip(a)}
        <div class="master">
          <div class="meter"><div class="meter-rms"></div><div class="meter-peak"></div></div>
          <div class="master-body">
            <div class="master-fader" id="mx-master"></div>
            <div class="rec">
              <button id="mx-rec" type="button">Record</button>
              <span id="mx-rec-status" class="muted num"></span>
            </div>
          </div>
        </div>
        ${strip(b)}
      </div>
      <div class="crossfader-row">
        <span class="xf-end muted">${escapeHtml(a)}</span>
        <div class="xf-track">
          <input id="mx-xfader" type="range" min="-1" max="1" step="0.005" value="0"
                 aria-label="Crossfader" />
          <div class="xf-detent"></div>
        </div>
        <span class="xf-end muted">${escapeHtml(b)}</span>
      </div>
      <div id="mx-note" class="mixer-note"></div>
    `;
  }

  private buildControls(): void {
    const grab = () => this.onManualControl?.();

    for (const id of this.channelIds) {
      const eqRow = this.root.querySelector(`[data-eq-row="${id}"]`) as HTMLElement;
      for (const band of EQ_BANDS) {
        const knob = new Knob({
          label: BAND_LABELS[band],
          value: 0.5,
          defaultValue: 0.5,
          bipolar: true,
          format: formatDb,
          onGrab: grab,
          onChange: (value) => this.mixer.channel(id).setEq(band, value),
        });
        eqRow.appendChild(knob.element);
        this.eqKnobs.set(`${id}:${band}`, knob);
      }

      const trimRow = this.root.querySelector(`[data-trim-row="${id}"]`) as HTMLElement;
      const fader = new Fader({
        label: "VOL",
        value: 0.8,
        defaultValue: 0.8,
        format: formatLevel,
        onGrab: grab,
        onChange: (value) => this.mixer.channel(id).setTrim(value),
      });
      trimRow.appendChild(fader.element);
      this.trimFaders.set(id, fader);
    }

    this.masterFader = new Fader({
      label: "MASTER",
      value: 0.8,
      defaultValue: 0.8,
      format: formatLevel,
      onGrab: grab,
      onChange: (value) => this.mixer.setMaster(value),
    });
    (this.root.querySelector("#mx-master") as HTMLElement).appendChild(this.masterFader.element);
  }

  private $<T extends HTMLElement = HTMLElement>(selector: string): T {
    const node = this.root.querySelector(selector);
    if (!node) throw new Error(`missing ${selector} in mixer`);
    return node as T;
  }

  private bind(): void {
    this.root.addEventListener("pointerdown", () => this.onManualControl?.(), true);

    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-act="eq-reset"]')) {
      button.addEventListener("click", () => {
        const id = button.dataset["ch"] as string;
        for (const band of EQ_BANDS) this.eqKnobs.get(`${id}:${band}`)?.reset();
      });
    }

    const crossfader = this.$<HTMLInputElement>("#mx-xfader");
    crossfader.addEventListener("input", () => {
      this.mixer.setCrossfader(Number(crossfader.value));
    });
    crossfader.addEventListener("dblclick", () => this.setCrossfader(0));

    this.$("#mx-rec").addEventListener("click", () => void this.toggleRecording());

    this.$("#mx-collapse").addEventListener("click", () => {
      this.setCollapsed(!this.collapsed, true);
    });
  }

  /**
   * Hide the strips and crossfader.
   *
   * Auto-mix drives all of this itself, so the controls are dead weight while
   * it runs - and on a laptop they are what pushes the library off the screen.
   * An explicit toggle is remembered; an automatic one is not, so arming does
   * not quietly overwrite a preference.
   */
  setCollapsed(collapsed: boolean, remember = false): void {
    this.collapsed = collapsed;
    this.root.classList.toggle("collapsed", collapsed);
    const button = this.$<HTMLButtonElement>("#mx-collapse");
    button.textContent = collapsed ? "Show" : "Hide";
    button.setAttribute("aria-expanded", String(!collapsed));
    if (remember) {
      try {
        localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
      } catch {
        /* private browsing */
      }
    }
  }

  isCollapsed(): boolean {
    return this.collapsed;
  }

  setCrossfader(position: number): void {
    this.mixer.setCrossfader(position);
    this.$<HTMLInputElement>("#mx-xfader").value = String(this.mixer.getCrossfader());
  }

  nudgeCrossfader(delta: number): void {
    this.setCrossfader(this.mixer.getCrossfader() + delta);
  }

  private async toggleRecording(): Promise<void> {
    const state = this.recorder.state();
    if (!state.supported) return;

    if (state.recording) {
      const blob = await this.recorder.stop();
      if (blob) {
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        downloadBlob(blob, `mix-${stamp}.${this.recorder.extension()}`);
      }
    } else {
      this.recorder.start();
    }
    this.render();
  }

  /** Called from the shared animation loop. */
  renderFrame(): void {
    // Nothing to draw while hidden, and the analyser read is per-frame work.
    if (this.collapsed) return;
    const { peak, rms, clipping } = this.mixer.read();
    const now = performance.now();

    if (peak >= this.peakHold || now > this.peakHoldUntil) {
      this.peakHold = peak;
      this.peakHoldUntil = now + 800;
    }
    if (clipping) this.clipUntil = now + 1200;

    // dBFS mapped over a 60 dB window, which is the useful range for a mix
    // meter; a linear amplitude bar spends most of its travel near silence.
    const toPercent = (value: number) => {
      if (value <= 0) return 0;
      const db = 20 * Math.log10(value);
      return Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
    };

    this.$(".meter-rms").style.width = `${toPercent(rms)}%`;
    this.$(".meter-peak").style.left = `${toPercent(this.peakHold)}%`;
    this.$(".meter").classList.toggle("clipping", now < this.clipUntil);

    // The crossfader is driven by auto-mix as well as by hand, so keep the
    // control in step with the value it is displaying.
    const crossfader = this.$<HTMLInputElement>("#mx-xfader");
    if (document.activeElement !== crossfader) {
      const actual = this.mixer.observedCrossfader().toFixed(3);
      if (crossfader.value !== actual) crossfader.value = actual;
    }

    const state = this.recorder.state();
    if (state.recording) {
      const megabytes = state.bytes / 1e6;
      this.$("#mx-rec-status").textContent =
        `${formatDuration(state.seconds)} · ${megabytes.toFixed(1)} MB`;
      if (state.seconds > LONG_RECORDING_SECONDS) {
        this.$("#mx-note").innerHTML =
          `<span class="warn">Long recording — it is held in memory until you stop and save it.</span>`;
      }
    }
  }

  private render(): void {
    const state = this.recorder.state();
    const button = this.$<HTMLButtonElement>("#mx-rec");
    button.textContent = state.recording ? "Stop & save" : "Record";
    button.classList.toggle("recording", state.recording);
    button.disabled = !state.supported;

    const note = this.$("#mx-note");
    if (state.error) {
      note.innerHTML = `<span class="bad">${escapeHtml(state.error)}</span>`;
    } else if (state.recording) {
      note.innerHTML = `<span class="muted">Recording the master bus in real time.</span>`;
    } else {
      note.textContent = "";
      this.$("#mx-rec-status").textContent = "";
    }
  }
}
